/**
 * OpacitySlider Component
 *
 * Interactive slider for controlling layer opacity.
 */

import React, { useCallback, useState } from 'react';
import './OpacitySlider.scss';
import { formatOpacity } from '../utils/layerDrawerUtils';

interface OpacitySliderProps {
  layerId: string;
  layerName: string;
  opacity: number;
  onChange: (opacity: number) => void;
  disabled?: boolean;
}

export const OpacitySlider: React.FC<OpacitySliderProps> = ({
  layerId,
  layerName,
  opacity,
  onChange,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localOpacity, setLocalOpacity] = useState(opacity);

  /**
   * Handle slider change
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseFloat(e.target.value);
    setLocalOpacity(newOpacity);

    // Debounce the callback during drag
    if (!isDragging) {
      onChange(newOpacity);
    }
  }, [isDragging, onChange]);

  /**
   * Handle mouse/touch start
   */
  const handlePointerDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  /**
   * Handle mouse/touch end
   */
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    onChange(localOpacity);
  }, [localOpacity, onChange]);

  /**
   * Sync local opacity with prop changes
   */
  React.useEffect(() => {
    if (!isDragging) {
      setLocalOpacity(opacity);
    }
  }, [opacity, isDragging]);

  return (
    <div className="opacity-slider">
      <label htmlFor={`opacity-${layerId}`} className="opacity-slider-label">
        <span className="opacity-slider-label-text">Opacity</span>
        <span className="opacity-slider-value">{formatOpacity(localOpacity)}</span>
      </label>
      <input
        id={`opacity-${layerId}`}
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={localOpacity}
        onChange={handleChange}
        onMouseDown={handlePointerDown}
        onMouseUp={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchEnd={handlePointerUp}
        disabled={disabled}
        className="opacity-slider-input"
        aria-label={`Opacity for ${layerName}`}
      />
    </div>
  );
};
